import { FC, useMemo, useContext, useEffect, useState } from "react";

import { Dropdown } from "capitalrx-components-2.0/lib/forms";
import { Grid, Stack, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Pager, DataRow, DataSource, Paged, Spinner, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { TextColor } from "capitalrx-components-2.0/lib/typography";
import { Color } from "capitalrx-components-2.0/lib/general";

import { Auth, getPCDocumentList, PharmacyContracting } from "@/api";
import { AuthenticationContext } from "@/context";

import ProviderDocumentRow from "./ProviderDocumentRow";
import styles from "./DocumentsList.module.scss";


interface ProviderDocumentsFormProps {
  user: Auth.GetUserResponse;
}

export const ProviderDocumentsForm: FC<ProviderDocumentsFormProps> = ({ user }) => {
  const { getAuthToken } = useContext(AuthenticationContext);

  const [totalDocuments, setTotalDocuments] = useState<string>("0");

  const RESULTS_PER_PAGE = 10;

  const currentYear: number = (new Date()).getFullYear();
  const years = [`${currentYear-2}`, `${currentYear-1}`, `${currentYear}`];
  const monthNames = ["All", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const [year, setYear] = useState<typeof years[number]>(`${currentYear}`);
  const [month, setMonth] = useState<typeof monthNames[number]>("All");
  const [documentType, setDocumentType] = useState<PharmacyContracting.ProviderDocumentType>("All");
  const [results, setResults] = useState<PharmacyContracting.DocumentResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const { three_six_twelve_columns } = useResponsive({
    three_six_twelve_columns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
  });

  useEffect(() => {
    const fetchCases = async () => {
      setLoading(true);
      try {
        const authToken = getAuthToken();
        const requestPayload = {
          document_type: documentType,
        };
        if (month !== "All") {
          requestPayload["created_at_start_date"] = `${year}-${monthNames.indexOf(month)}-01`;
          if (month == "December") {
            requestPayload["created_at_end_date"] = `${parseInt(year)+1}-01-01`;
          } else{
            requestPayload["created_at_end_date"] = `${year}-${monthNames.indexOf(month)+1}-01`;
          }
        } else {
          requestPayload["created_at_start_date"] = `${year}-01-01`;
          requestPayload["created_at_end_date"] = `${year}-12-31`;
        }

        const response = await getPCDocumentList(requestPayload, authToken);

        setResults(response.results);
        setTotalDocuments(response.result_count);

      } catch (error) {
        console.error("Failed to fetch documents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [year, month, documentType, page]);


  const allRows: ReadonlyArray<DataRow<PharmacyContracting.DocumentResponse>> = useMemo(
    () =>
      results.map((item, index) => ({
        id: index,
        data: item,
      })),
    [results],
  );

  const pagedRows = useMemo(() => {
    const start = page * RESULTS_PER_PAGE;
    return allRows.slice(start, start + RESULTS_PER_PAGE);
  }, [allRows, page]);

  const baseDataSource = useSimpleTableDataSource<PharmacyContracting.DocumentResponse>(pagedRows);

  const dataSource: DataSource<object> & Paged = {
    ...baseDataSource,
    page: page,
    pageSize: RESULTS_PER_PAGE,
    totalRows: allRows.length,
    maxRows: allRows.length,
    hasNextPage: (page + 1) * RESULTS_PER_PAGE < allRows.length,
    first: () => setPage(1),
    previous: () => setPage(Math.max(page - 1, 0)),
    next: () => {
      const nextPage = (page + 1) * RESULTS_PER_PAGE < allRows.length ? page + 1 : page;
      setPage(nextPage);
    },
    last: () => setPage(Math.floor((allRows.length - 1) / RESULTS_PER_PAGE)),
  };

  return (
    <Stack spacing="large">
      <Grid>
        <Grid.Span columns={three_six_twelve_columns}>
          <Grid>
            <Grid.Span columns={three_six_twelve_columns}>
              <Dropdown
                label="Year"
                onChange={(value) => setYear((value || "") as string)}
                value={year}
              >
                {years.map((yr) => (<Dropdown.Option key={yr} value={yr}>{yr}</Dropdown.Option>))}
              </Dropdown>
            </Grid.Span>
            <Grid.Span columns={three_six_twelve_columns}>
              <Dropdown
                label="Month"
                defaultValue="All"
                onChange={(value) => setMonth((value || "") as string)}
                value={month}
              >
                {monthNames.map((month) => (<Dropdown.Option key={month} value={month}>{month}</Dropdown.Option>))}
              </Dropdown>
            </Grid.Span>
          </Grid>
        </Grid.Span>
        <Grid.Span columns={three_six_twelve_columns}>
          <Dropdown
            label="Document Type"
            onChange={(value) => setDocumentType((value || "") as string)}
            value={documentType}
          >
            {PharmacyContracting.ProviderDocumentTypeValues.map((docType) => (<Dropdown.Option key={docType} value={docType}>{docType}</Dropdown.Option>))}
          </Dropdown>
        </Grid.Span>
      </Grid>
      {loading && (
        <Stack className={styles.LoadingContainer} alignment="center">
          <Spinner className={styles.LoadingSpinner} size="large" />
        </Stack>
      )}
      {!loading && (
        <Stack>
          <Shelf>
            <TextColor appearance={Color.DarkGrey[400]}>{`${totalDocuments} DOCUMENTS`}</TextColor>
            <Shelf.Spacer />
          </Shelf>
          <Stack spacing="small">
            {pagedRows.map((row) => (
              <ProviderDocumentRow
                key={row.data.document.id}
                DocumentUUID={row.data.document.id}
                DocumentType={row.data.document.content_type}
                DocumentName={row.data.document.name}
                DocumentEffectiveDate={row.data.document.effective_date}
              />
            ))}
            {allRows.length > 0 && <Pager dataSource={dataSource} />}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default ProviderDocumentsForm;
